import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft, Check, CheckCircle2, Download, FileSpreadsheet, ListChecks, Loader2, PenLine, Search,
  Upload, XCircle,
} from 'lucide-react';
import { masterAPI } from '@/lib/api';
import { readFileAsBase64, saveBlob } from '@/lib/files';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';

const CATEGORIES = ['insecticide', 'herbicide', 'fungicide', 'fertilizer', 'seed', 'veterinary_drug',
  'vaccine', 'equipment', 'other'];
const UNITS = ['piece', 'kg', 'g', 'litre', 'ml', 'bag', 'pack', 'bottle', 'carton', 'vial', 'dose'];
const TABS = [
  { key: 'list', icon: ListChecks },
  { key: 'manual', icon: PenLine },
  { key: 'upload', icon: FileSpreadsheet },
];

function errorsOf(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (detail?.errors) return detail.errors;
  if (typeof detail === 'string') return [detail];
  if (Array.isArray(detail) && detail[0]?.msg) return [detail[0].msg.replace(/^Value error, /, '')];
  return [fallback];
}

/**
 * Add products to my stock, three ways: tick them on the NAFDAC list,
 * type one in, or upload a spreadsheet. A NAFDAC number and an expiry
 * date that hasn't passed are required; numbers already on the list use
 * the list's details.
 */
export default function AddProductsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((x) => x.key === params.get('tab')) ? params.get('tab') : 'list';

  return (
    <div className="p-4 md:p-6 animate-fadeIn">
      <button type="button" onClick={() => navigate('/inventory')}
              className="flex items-center gap-1 text-gray-700 font-semibold h-10 -ml-1 mb-2">
        <ArrowLeft className="w-5 h-5" /> {t('myProducts.backToStock')}
      </button>
      <h1 className="font-heading text-2xl font-bold">{t('myProducts.title')}</h1>
      <p className="text-muted-foreground mb-4">{t('myProducts.subtitle')}</p>

      <div className="grid grid-cols-3 gap-2 mb-5" role="tablist">
        {TABS.map(({ key, icon: Icon }) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            onClick={() => setParams({ tab: key }, { replace: true })}
            className={`flex flex-col items-center gap-1 rounded-xl border-2 px-2 py-3 text-sm font-semibold ${
              tab === key ? 'border-farm-primary bg-farm-primary/10 text-farm-primary' : 'border-gray-200 bg-white text-gray-700'}`}
            data-testid={`add-products-tab-${key}`}
          >
            <Icon className="w-6 h-6" />
            {t(`myProducts.tabs.${key}`)}
          </button>
        ))}
      </div>

      {tab === 'list' && <FromListTab />}
      {tab === 'manual' && <ManualTab />}
      {tab === 'upload' && <UploadTab />}
    </div>
  );
}

function FromListTab() {
  // After adding, the next screen is the stock list (beta feedback).
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setQuery(q.trim()), 300);
    return () => clearTimeout(timer);
  }, [q]);

  const load = useCallback(async (offset = 0) => {
    setLoading(true);
    try {
      const res = await masterAPI.search({ q: query, limit: 30, offset });
      setItems((prev) => (offset ? [...prev, ...res.data.items] : res.data.items));
      setHasMore(res.data.has_more);
    } catch {
      toast({ title: t('myProducts.loadFailed'), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [query, t, toast]);

  useEffect(() => { load(0); }, [load]);

  const ids = Object.keys(picked).filter((id) => picked[id]);
  const add = async () => {
    setSaving(true);
    try {
      const res = await masterAPI.addFromList(ids);
      const { added, already, skipped } = res.data;
      toast({ title: t('myProducts.list.added', { n: added.length }),
              description: already.length || skipped.length
                ? t('myProducts.list.addedNote', { already: already.length, skipped: skipped.length }) : undefined });
      setPicked({});
      if (added.length) navigate('/inventory');
      else load(0);
    } catch (err) {
      toast({ title: errorsOf(err, t('myProducts.saveFailed'))[0], variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pb-24">
      <div className="relative mb-3">
        <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} className="h-12 pl-10 bg-white text-base"
               placeholder={t('myProducts.list.searchPlaceholder')} data-testid="master-search" />
      </div>
      <p className="text-sm text-muted-foreground mb-3">{t('myProducts.list.hint')}</p>
      <ul className="space-y-2" data-testid="master-results">
        {items.map((item) => {
          const disabled = item.expired || item.in_my_stock;
          return (
            <li key={item.id}>
              <label className={`flex items-start gap-3 rounded-xl border bg-white px-4 py-3 ${
                disabled ? 'opacity-60' : 'cursor-pointer'} ${picked[item.id] ? 'border-farm-primary' : ''}`}>
                <input type="checkbox" className="mt-1 w-5 h-5 accent-[#2e7d32]" disabled={disabled}
                       checked={!!picked[item.id]}
                       onChange={(e) => setPicked({ ...picked, [item.id]: e.target.checked })}
                       data-testid={`master-pick-${item.nafdac_number}`} />
                <span className="flex-1 min-w-0">
                  <span className="block font-semibold break-words">{item.name}</span>
                  <span className="block text-sm text-gray-600">
                    NAFDAC {item.nafdac_number}
                    {item.nafdac_expiry && ` · ${t('myProducts.expires', { date: item.nafdac_expiry })}`}
                  </span>
                  {(item.manufacturer_name || item.active_ingredients) && (
                    <span className="block text-xs text-gray-500 break-words">
                      {[item.manufacturer_name, item.active_ingredients].filter(Boolean).join(' · ')}
                    </span>
                  )}
                </span>
                {item.in_my_stock && <Badge tone="green">{t('myProducts.list.inStock')}</Badge>}
                {item.expired && <Badge tone="red">{t('myProducts.expired')}</Badge>}
                {item.master_status === 'pending' && <Badge tone="amber">{t('myProducts.pending')}</Badge>}
              </label>
            </li>
          );
        })}
      </ul>
      {!loading && items.length === 0 && (
        <p className="text-center text-gray-600 py-8">{t('myProducts.list.none')}</p>
      )}
      {loading && <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-farm-primary" /></div>}
      {hasMore && !loading && (
        <Button variant="outline" className="w-full mt-3" onClick={() => load(items.length)}>
          {t('myProducts.list.more')}
        </Button>
      )}
      <div className="fixed left-0 right-0 bottom-20 px-4 z-30">
        <div className="max-w-lg md:max-w-3xl lg:max-w-5xl mx-auto">
          <Button className="w-full h-14 text-base font-bold bg-farm-primary hover:bg-farm-primary-dark shadow-lg"
                  disabled={!ids.length || saving} onClick={add} data-testid="master-add-selected">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : t('myProducts.list.addSelected', { n: ids.length })}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Badge({ tone, children }) {
  const tones = { green: 'bg-green-100 text-green-800', red: 'bg-red-100 text-red-700', amber: 'bg-amber-100 text-amber-800' };
  return <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${tones[tone]}`}>{children}</span>;
}

const EMPTY = { nafdac_number: '', nafdac_expiry: '', name: '', category: '', unit: '', unit_price: '',
  quantity: '', net_contents: '', active_ingredients: '', manufacturer_name: '' };

function ManualTab() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (key) => (e) => setForm({ ...form, [key]: e.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrors([]);
    const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== ''));
    try {
      const res = await masterAPI.addManual(payload);
      toast({
        title: t('myProducts.manual.added', { name: res.data.name }),
        description: res.data.match === 'master' ? t('myProducts.manual.usedList') : t('myProducts.manual.sentForReview'),
      });
      setForm(EMPTY);
      navigate('/inventory');
    } catch (err) {
      setErrors(errorsOf(err, t('myProducts.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  const field = (key, label, props = {}) => (
    <div className="space-y-1.5">
      <Label htmlFor={`mp-${key}`} className="text-base">{label}</Label>
      <Input id={`mp-${key}`} value={form[key]} onChange={set(key)} className="h-12 bg-white text-base"
             data-testid={`manual-${key}`} {...props} />
    </div>
  );

  return (
    <form onSubmit={submit} className="space-y-4 pb-6">
      <p className="text-sm text-muted-foreground">{t('myProducts.manual.hint')}</p>
      {field('nafdac_number', t('myProducts.fields.nafdacNumber'), { placeholder: 'A10-0907', autoCapitalize: 'characters' })}
      {field('nafdac_expiry', t('myProducts.fields.nafdacExpiry'), { type: 'date' })}
      {field('name', t('myProducts.fields.name'))}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-base">{t('myProducts.fields.category')}</Label>
          <Select value={form.category} onValueChange={set('category')}>
            <SelectTrigger className="h-12 bg-white" data-testid="manual-category"><SelectValue placeholder={t('myProducts.choose')} /></SelectTrigger>
            <SelectContent className="bg-white">
              {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{t(`products.categories.${c}`, c)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-base">{t('myProducts.fields.unit')}</Label>
          <Select value={form.unit} onValueChange={set('unit')}>
            <SelectTrigger className="h-12 bg-white" data-testid="manual-unit"><SelectValue placeholder={t('myProducts.choose')} /></SelectTrigger>
            <SelectContent className="bg-white">
              {UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {field('unit_price', t('myProducts.fields.price'), { type: 'number', min: 0, inputMode: 'decimal' })}
        {field('quantity', t('myProducts.fields.quantity'), { type: 'number', min: 0, step: 1, inputMode: 'numeric' })}
      </div>
      {field('net_contents', t('myProducts.fields.packSize'))}
      {field('active_ingredients', t('myProducts.fields.activeIngredients'))}
      {field('manufacturer_name', t('myProducts.fields.manufacturer'))}
      {errors.length > 0 && (
        <ul className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700 space-y-1" data-testid="manual-errors">
          {errors.map((e) => <li key={e}>• {e}</li>)}
        </ul>
      )}
      <Button type="submit" disabled={saving} className="w-full h-14 text-base font-bold bg-farm-primary hover:bg-farm-primary-dark"
              data-testid="manual-save">
        {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : t('myProducts.manual.save')}
      </Button>
    </form>
  );
}

function UploadTab() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const template = async () => {
    try {
      const res = await masterAPI.template();
      saveBlob(res.data, 'African-Farm-My-Products.xlsx');
    } catch {
      toast({ title: t('myProducts.upload.templateFailed'), variant: 'destructive' });
    }
  };

  const run = async (commit, picked = file) => {
    setBusy(true);
    setError('');
    try {
      const content = await readFileAsBase64(picked);
      const res = await masterAPI.upload(picked.name, content, commit);
      setReport(res.data);
      if (commit) {
        toast({ title: t('myProducts.upload.done', { n: res.data.summary.ok }) });
        if (res.data.summary.ok) navigate('/inventory');
      }
    } catch (err) {
      setReport(null);
      setError(errorsOf(err, t('myProducts.upload.failed'))[0]);
    } finally {
      setBusy(false);
    }
  };

  const choose = (e) => {
    const picked = e.target.files?.[0];
    e.target.value = '';
    if (!picked) return;
    setFile(picked);
    run(false, picked);
  };

  const summary = report?.summary;
  return (
    <div className="space-y-4 pb-6">
      <Step n={1} title={t('myProducts.upload.step1')} text={t('myProducts.upload.step1Text')}>
        <Button variant="outline" className="h-12 w-full bg-white" onClick={template} data-testid="upload-template">
          <Download className="w-5 h-5 mr-2" /> {t('myProducts.upload.download')}
        </Button>
      </Step>
      <Step n={2} title={t('myProducts.upload.step2')} text={t('myProducts.upload.step2Text')}>
        <input ref={fileRef} type="file" accept=".xlsx,.csv" className="hidden" onChange={choose}
               data-testid="upload-file" />
        <Button className="h-12 w-full bg-farm-primary hover:bg-farm-primary-dark" disabled={busy}
                onClick={() => fileRef.current?.click()} data-testid="upload-choose">
          {busy && !report ? <Loader2 className="w-5 h-5 animate-spin" />
            : <><Upload className="w-5 h-5 mr-2" /> {file ? file.name : t('myProducts.upload.choose')}</>}
        </Button>
        {error && <p className="text-sm text-red-700 mt-2" data-testid="upload-error">{error}</p>}
      </Step>

      {report && (
        <div className="rounded-2xl border bg-white p-4" data-testid="upload-report">
          <p className="font-semibold mb-1">
            {report.committed
              ? t('myProducts.upload.addedSummary', { n: summary.ok })
              : t('myProducts.upload.checkSummary', { total: summary.total, ok: summary.ok, errors: summary.errors })}
          </p>
          {!report.committed && summary.ok > 0 && (
            <p className="text-sm text-gray-600 mb-3">
              {t('myProducts.upload.matchNote', { onList: summary.on_list, fresh: summary.new })}
            </p>
          )}
          <ul className="divide-y max-h-[50vh] overflow-y-auto">
            {report.rows.map((row) => (
              <li key={row.row} className="py-2 flex gap-2 text-sm" data-testid={`upload-row-${row.row}`}>
                {row.ok ? <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
                  : <XCircle className="w-5 h-5 text-red-600 shrink-0" />}
                <div className="min-w-0">
                  <p className="font-medium break-words">
                    {t('myProducts.upload.row', { n: row.row })}: {row.name || '—'}
                    {row.nafdac_number ? ` (${row.nafdac_number})` : ''}
                  </p>
                  {row.ok ? (
                    <p className="text-gray-600">
                      {row.match === 'master' ? t('myProducts.upload.onList') : t('myProducts.upload.newForReview')}
                    </p>
                  ) : row.errors.map((e) => <p key={e} className="text-red-700">{e}</p>)}
                </div>
              </li>
            ))}
          </ul>
          {!report.committed && (
            <Button className="w-full h-14 mt-3 text-base font-bold bg-farm-primary hover:bg-farm-primary-dark"
                    disabled={busy || summary.ok === 0} onClick={() => run(true)} data-testid="upload-commit">
              {busy ? <Loader2 className="w-5 h-5 animate-spin" />
                : <><Check className="w-5 h-5 mr-2" />{t('myProducts.upload.add', { n: summary.ok })}</>}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function Step({ n, title, text, children }) {
  return (
    <div className="rounded-2xl border bg-white p-4">
      <p className="font-semibold"><span className="text-farm-primary mr-1">{n}.</span>{title}</p>
      <p className="text-sm text-gray-600 mb-3">{text}</p>
      {children}
    </div>
  );
}
